import React, { useRef, useState } from "react";
import { G, UI, useStore, closeWindow, useIsGM, controlsChar, controlsObj, type Win } from "../store";
import { update, remove, setGame, send, create } from "../net";
import { Field, Markdown, confirm, prompt, uid } from "../util";
import { Sheet5e } from "../sheets/Sheet5e";
import type { Char, Handout, Obj, Page, RollTable } from "../../shared/types";
import { ALL_MARKERS, MARKERS, COLOR_MARKERS } from "../board/images";
import { sendChat } from "../chatSend";
import { resetExplored } from "../board/Board";
import { AssetPicker } from "./Art";
import { blank5e } from "../../shared/sheet5e";

export function Windows() {
  const wins = useStore(UI, s => s.windows);
  return <>{wins.map(w => <WindowFrame key={w.id} w={w} />)}</>;
}

function WindowFrame({ w }: { w: Win }) {
  const recs = useStore(G, s => s.recs);
  const ref = useRef<HTMLDivElement>(null);
  let title = "";
  let body: React.ReactNode = null;
  switch (w.kind) {
    case "char": {
      const ch = recs.char[w.ref];
      if (!ch) body = <div className="pad dim">This character isn't available.</div>;
      else { title = ch.name; body = <CharWindow ch={ch} />; }
      break;
    }
    case "handout": {
      const h = recs.handout[w.ref];
      if (!h) body = <div className="pad dim">This handout isn't available.</div>;
      else { title = h.name; body = <HandoutWindow h={h} />; }
      break;
    }
    case "token": {
      const o = recs.obj[w.ref];
      if (!o) body = <div className="pad dim">This token was removed.</div>;
      else { title = `Token: ${o.name || "(unnamed)"}`; body = <TokenEditor o={o} />; }
      break;
    }
    case "page": {
      const p = recs.page[w.ref];
      if (!p) body = <div className="pad dim">This page was deleted.</div>;
      else { title = `Page settings: ${p.name}`; body = <PageSettings p={p} close={() => closeWindow(w.id)} />; }
      break;
    }
    case "table": {
      const t = recs.table[w.ref];
      if (!t) body = <div className="pad dim">This table was deleted.</div>;
      else { title = `Rollable table: ${t.name}`; body = <TableEditor t={t} />; }
      break;
    }
  }
  const front = () => {
    const ws = UI.get().windows;
    const z = Math.max(...ws.map(x => x.z)) + 1;
    if (w.z < z - 1) UI.set({ windows: ws.map(x => (x.id === w.id ? { ...x, z } : x)) });
  };
  const startDrag = (e: React.PointerEvent, mode: "move" | "resize") => {
    e.preventDefault();
    front();
    const sx = e.clientX, sy = e.clientY, s = { ...w };
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      const patch = mode === "move"
        ? { x: Math.max(-s.w + 80, Math.min(window.innerWidth - 80, s.x + dx)), y: Math.max(0, Math.min(window.innerHeight - 40, s.y + dy)) }
        : { w: Math.max(300, s.w + dx), h: Math.max(200, s.h + dy) };
      UI.set({ windows: UI.get().windows.map(x => (x.id === w.id ? { ...x, ...patch } : x)) });
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <div ref={ref} className="win" style={{ left: w.x, top: w.y, width: w.w, height: w.h, zIndex: w.z }} onPointerDown={front}>
      <div className="win-title" onPointerDown={e => startDrag(e, "move")}>
        <span>{title}</span>
        <button onPointerDown={e => e.stopPropagation()} onClick={() => closeWindow(w.id)}>✕</button>
      </div>
      <div className="win-body">{body}</div>
      <div className="win-resize" onPointerDown={e => startDrag(e, "resize")} />
    </div>
  );
}

// ---------------- character ----------------
function CharWindow({ ch }: { ch: Char }) {
  const gm = useIsGM();
  const sheet = useStore(G, s => s.game?.settings.sheet || "dnd5e");
  const editable = controlsChar(ch);
  const [tab, setTab] = useState<"sheet" | "bio" | "attrs">(sheet === "dnd5e" && ch.sheet !== "none" ? "sheet" : "bio");
  return (
    <div className="charwin">
      <div className="tabs">
        {sheet === "dnd5e" && ch.sheet !== "none" && <button className={tab === "sheet" ? "on" : ""} onClick={() => setTab("sheet")}>Character sheet</button>}
        <button className={tab === "bio" ? "on" : ""} onClick={() => setTab("bio")}>Bio & info</button>
        <button className={tab === "attrs" ? "on" : ""} onClick={() => setTab("attrs")}>Attributes & abilities</button>
      </div>
      <div className="tab-body">
        {tab === "sheet" && <Sheet5e ch={ch} editable={editable} />}
        {tab === "bio" && <BioTab ch={ch} editable={editable} gm={gm} />}
        {tab === "attrs" && <AttrsTab ch={ch} editable={editable} />}
      </div>
    </div>
  );
}

function BioTab({ ch, editable, gm }: { ch: Char; editable: boolean; gm: boolean }) {
  const [edit, setEdit] = useState(false);
  const members = useStore(G, s => s.members);
  return (
    <div className="pad bio">
      <div className="bio-top">
        <div className="avatar-box">
          {ch.avatar ? <img src={ch.avatar} alt="" /> : <div className="avatar-empty">No avatar</div>}
          {editable && <AssetPicker label="Set avatar" onPick={url => update("char", ch.id, { avatar: url })} />}
        </div>
        <div className="grow">
          <label>Name <Field value={ch.name} onCommit={v => update("char", ch.id, { name: v })} disabled={!editable} /></label>
          {gm && <label>Folder <Field value={ch.folder || ""} placeholder="e.g. NPCs/Goblins" onCommit={v => update("char", ch.id, { folder: v })} /></label>}
          {gm && (
            <>
              <Perms label="In players' journals" list={ch.inJournals} members={members} onChange={l => update("char", ch.id, { inJournals: l })} />
              <Perms label="Can be edited & controlled by" list={ch.controlledBy} members={members} onChange={l => update("char", ch.id, { controlledBy: l })} />
              <label className="chk"><input type="checkbox" checked={ch.sheet === "none"} onChange={e => update("char", ch.id, { sheet: e.target.checked ? "none" : "dnd5e" })} /> No sheet (attributes only)</label>
              <label className="chk"><input type="checkbox" checked={!!ch.archived} onChange={e => update("char", ch.id, { archived: e.target.checked })} /> Archived</label>
            </>
          )}
          {editable && <DefaultTokenButton ch={ch} />}
        </div>
      </div>
      <div className="box-title">Bio {editable && <button className="mini" onClick={() => setEdit(!edit)}>{edit ? "Done" : "Edit"}</button>}</div>
      {edit ? <Field multiline rows={10} value={ch.bio || ""} onCommit={v => update("char", ch.id, { bio: v })} /> : <Markdown text={ch.bio || "_No bio yet._"} />}
      {gm && (
        <>
          <div className="box-title">GM notes <span className="dim small">(only GMs see this)</span></div>
          <Field multiline rows={5} value={ch.gmNotes || ""} onCommit={v => update("char", ch.id, { gmNotes: v })} />
          <div className="row gap">
            <button onClick={async () => {
              const { id: _i, ...data } = ch;
              await create("char", { ...data, name: ch.name + " (copy)" });
            }}>Duplicate</button>
            <button className="danger" onClick={async () => { if (await confirm(`Delete ${ch.name}? This can't be undone.`)) remove("char", ch.id); }}>Delete character</button>
          </div>
        </>
      )}
    </div>
  );
}

function DefaultTokenButton({ ch }: { ch: Char }) {
  const sel = useStore(UI, s => s.selected);
  const objs = useStore(G, s => s.recs.obj);
  const tok = sel.map(id => objs[id]).find(o => o && o.type === "image" && o.represents === ch.id);
  if (!tok) return <div className="dim small">Tip: select a token that represents this character to set it as the default token. Drag the character from the journal onto the map to place it.</div>;
  return (
    <button onClick={() => {
      const { id, x, y, pageId, z, layer, ...rest } = tok;
      update("char", ch.id, { defaultToken: rest });
    }}>Use selected token as default token</button>
  );
}

export function Perms({ label, list, members, onChange }: { label: string; list: string[]; members: any[]; onChange: (l: string[]) => void }) {
  const all = list.includes("all");
  const toggle = (id: string) => onChange(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  return (
    <div className="perms">
      <div className="small">{label}</div>
      <label className="chk"><input type="checkbox" checked={all} onChange={() => toggle("all")} /> All players</label>
      {!all && members.filter(m => m.role !== "gm").map(m => (
        <label key={m.uid} className="chk"><input type="checkbox" checked={list.includes(m.uid)} onChange={() => toggle(m.uid)} /> {m.display}</label>
      ))}
    </div>
  );
}

function AttrsTab({ ch, editable }: { ch: Char; editable: boolean }) {
  const [newName, setNewName] = useState("");
  const keys = Object.keys(ch.attrs || {}).sort();
  const addAbility = () => update("char", ch.id, { abilities: [...(ch.abilities || []), { id: uid(), name: "New ability", action: "/r 1d20" }] });
  const setAb = (id: string, patch: any) => update("char", ch.id, { abilities: ch.abilities.map(a => (a.id === id ? { ...a, ...patch } : a)) });
  return (
    <div className="pad attrs">
      <div className="attrs-cols">
        <div>
          <div className="box-title">Attributes</div>
          <div className="dim small">Use in rolls as <code>@{"{"}{ch.name}|name{"}"}</code>. Keys ending in <code>_max</code> are read with <code>|max</code>.</div>
          <div className="attr-list">
            {keys.map(k => (
              <div key={k} className="attr-row">
                <span className="attr-k" title={k}>{k}</span>
                <Field value={ch.attrs[k]} onCommit={v => update("char", ch.id, { attrs: { [k]: v } })} disabled={!editable} />
                {editable && <button className="mini" onClick={() => update("char", ch.id, { attrs: { [k]: null } })}>✕</button>}
              </div>
            ))}
          </div>
          {editable && (
            <form className="row" onSubmit={e => { e.preventDefault(); const n = newName.trim().replace(/\s+/g, "_"); if (n) { update("char", ch.id, { attrs: { [n]: "0" } }); setNewName(""); } }}>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="new attribute name" />
              <button type="submit">Add</button>
            </form>
          )}
          {editable && ch.sheet !== "none" && keys.length === 0 && (
            <button onClick={() => update("char", ch.id, { attrs: blank5e() })}>Fill 5e defaults</button>
          )}
        </div>
        <div>
          <div className="box-title">Abilities {editable && <button className="mini" onClick={addAbility}>＋</button>}</div>
          <div className="dim small">Macros owned by this character. Bare <code>@{"{"}attr{"}"}</code> refers to this character. Call from anywhere with <code>%{"{"}{ch.name}|Ability{"}"}</code>.</div>
          {(ch.abilities || []).map(ab => (
            <div key={ab.id} className="ability">
              <div className="row">
                <button className="rollable" onClick={() => sendChat(ab.action, ch.id)}>▶</button>
                <Field value={ab.name} onCommit={v => setAb(ab.id, { name: v })} disabled={!editable} />
                <label className="chk small" title="Show in macro bar when a token for this character is selected">
                  <input type="checkbox" disabled={!editable} checked={!!ab.tokenAction} onChange={e => setAb(ab.id, { tokenAction: e.target.checked })} /> token action
                </label>
                {editable && <button className="mini" onClick={() => update("char", ch.id, { abilities: ch.abilities.filter(a => a.id !== ab.id) })}>✕</button>}
              </div>
              <Field multiline rows={2} value={ab.action} onCommit={v => setAb(ab.id, { action: v })} disabled={!editable} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- handout ----------------
function HandoutWindow({ h }: { h: Handout }) {
  const gm = useIsGM();
  const me = useStore(G, s => s.me);
  const members = useStore(G, s => s.members);
  const editable = gm || h.controlledBy.includes(me) || h.controlledBy.includes("all");
  const [edit, setEdit] = useState(false);
  return (
    <div className="pad handout">
      {h.image && <img className="handout-img" src={h.image} alt="" />}
      {editable && (
        <div className="row gap">
          <AssetPicker label={h.image ? "Change image" : "Add image"} onPick={url => update("handout", h.id, { image: url })} />
          {h.image && <button onClick={() => update("handout", h.id, { image: null })}>Remove image</button>}
          <button onClick={() => setEdit(!edit)}>{edit ? "Done editing" : "Edit"}</button>
          {gm && <button onClick={() => { update("handout", h.id, { inJournals: [...new Set([...h.inJournals, "all"])] }); setTimeout(() => send({ t: "fx", fx: { kind: "show", handout: h.id } }), 300); }}>📣 Show to players</button>}
        </div>
      )}
      {edit ? (
        <>
          <label>Name <Field value={h.name} onCommit={v => update("handout", h.id, { name: v })} /></label>
          <label>Notes (markdown) <Field multiline rows={12} value={h.notes || ""} onCommit={v => update("handout", h.id, { notes: v })} /></label>
          {gm && (
            <>
              <label>Folder <Field value={h.folder || ""} onCommit={v => update("handout", h.id, { folder: v })} /></label>
              <Perms label="In players' journals" list={h.inJournals} members={members} onChange={l => update("handout", h.id, { inJournals: l })} />
              <Perms label="Can be edited by" list={h.controlledBy} members={members} onChange={l => update("handout", h.id, { controlledBy: l })} />
              <label className="chk"><input type="checkbox" checked={!!h.archived} onChange={e => update("handout", h.id, { archived: e.target.checked })} /> Archived</label>
            </>
          )}
        </>
      ) : (
        <Markdown text={h.notes || ""} />
      )}
      {gm && (
        <>
          <div className="box-title">GM notes <span className="dim small">(only GMs see this)</span></div>
          <Field multiline rows={4} value={h.gmNotes || ""} onCommit={v => update("handout", h.id, { gmNotes: v })} />
          <button className="danger" onClick={async () => { if (await confirm(`Delete handout "${h.name}"?`)) remove("handout", h.id); }}>Delete handout</button>
        </>
      )}
    </div>
  );
}

// ---------------- token ----------------
function TokenEditor({ o }: { o: Obj }) {
  const gm = useIsGM();
  const chars = useStore(G, s => s.recs.char);
  const members = useStore(G, s => s.members);
  const page = useStore(G, s => s.recs.page[o.pageId]) as Page | undefined;
  const set = (patch: Partial<Obj>) => update("obj", o.id, patch);
  const bars = o.bars || [{ v: "", max: "" }, { v: "", max: "" }, { v: "", max: "" }];
  const setBar = (i: number, patch: any) => set({ bars: bars.map((b, j) => (j === i ? { ...b, ...patch } : b)) as any });
  const auras = o.auras || [];
  const setAura = (i: number, patch: any) => {
    const a = [...auras];
    while (a.length <= i) a.push({ r: 0, color: i ? "#3399ff" : "#ffcc00" });
    a[i] = { ...a[i], ...patch };
    set({ auras: a });
  };
  const ch = o.represents ? chars[o.represents] : undefined;
  const g = page?.grid || 70;
  const st = o.statuses || [];
  const toggleStatus = (s: string) => set({ statuses: st.some(x => x.split("@")[0] === s) ? st.filter(x => x.split("@")[0] !== s) : [...st, s] });
  return (
    <div className="pad token-ed">
      <div className="row gap">
        {o.src ? <img className="tok-prev" src={o.src} alt="" /> : <div className="tok-prev" />}
        <div className="grow">
          <label>Name <Field value={o.name || ""} onCommit={v => set({ name: v })} disabled={!gm} /></label>
          <div className="row gap wrap">
            <label className="chk"><input type="checkbox" checked={!!o.showName} onChange={e => set({ showName: e.target.checked })} /> Show nameplate</label>
            {gm && <label className="chk"><input type="checkbox" checked={o.namePlayers !== false} onChange={e => set({ namePlayers: e.target.checked })} /> Players see name</label>}
          </div>
          {gm && (
            <label>Represents character
              <select value={o.represents || ""} onChange={e => set({ represents: e.target.value || (null as any) })}>
                <option value="">— none —</option>
                {Object.values(chars).sort((a, b) => a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          {gm && <AssetPicker label="Change image" onPick={url => set({ src: url })} />}
        </div>
      </div>
      {gm && (
        <div className="row gap">
          <label>Width (cells) <input type="number" step="0.5" value={+(o.w / g).toFixed(2)} onChange={e => set({ w: Math.max(0.25, +e.target.value) * g })} /></label>
          <label>Height (cells) <input type="number" step="0.5" value={+(o.h / g).toFixed(2)} onChange={e => set({ h: Math.max(0.25, +e.target.value) * g })} /></label>
          <label>Tint <input type="color" value={o.tint || "#000000"} onChange={e => set({ tint: e.target.value })} /></label>
          {o.tint && <button className="mini" onClick={() => set({ tint: null as any })}>no tint</button>}
        </div>
      )}
      <div className="box-title">Bars</div>
      {bars.map((b, i) => (
        <div key={i} className="bar-row">
          <span className={`bar-dot b${i}`} />
          <Field value={b.v} onCommit={v => setBar(i, { v })} placeholder="value" />
          <span>/</span>
          <Field value={b.max} onCommit={v => setBar(i, { max: v })} placeholder="max" />
          {gm && (
            <>
              <select value={b.link || ""} title="Link to character attribute (keeps token and sheet in sync)" onChange={e => setBar(i, { link: e.target.value || undefined })} disabled={!ch}>
                <option value="">not linked</option>
                {ch && Object.keys(ch.attrs || {}).filter(k => !k.endsWith("_max")).sort().map(k => <option key={k} value={k}>{k}</option>)}
              </select>
              <label className="chk small"><input type="checkbox" checked={!!b.show} onChange={e => setBar(i, { show: e.target.checked })} /> players see</label>
            </>
          )}
        </div>
      ))}
      <div className="box-title">Status markers</div>
      <div className="status-grid">
        {ALL_MARKERS.map(s => {
          const on = st.find(x => x.split("@")[0] === s);
          return (
            <button key={s} title={s} className={on ? "on" : ""} onClick={() => toggleStatus(s)}
              onContextMenu={async e => {
                e.preventDefault();
                const n = await prompt(`Number on "${s}" marker (blank for none)`, on?.split("@")[1] || "");
                if (n === null) return;
                const rest = st.filter(x => x.split("@")[0] !== s);
                set({ statuses: [...rest, n ? `${s}@${n}` : s] });
              }}>
              {MARKERS[s] || <span className="cdot" style={{ background: COLOR_MARKERS[s] }} />}
            </button>
          );
        })}
      </div>
      <div className="dim small">Right-click a marker to put a number on it.</div>
      <div className="box-title">Auras</div>
      {[0, 1].map(i => (
        <div key={i} className="row gap">
          <label>Radius ({page?.units || "ft"}) <input type="number" value={auras[i]?.r || ""} onChange={e => setAura(i, { r: +e.target.value })} /></label>
          <input type="color" value={auras[i]?.color || (i ? "#3399ff" : "#ffcc00")} onChange={e => setAura(i, { color: e.target.value })} />
          <label className="chk small"><input type="checkbox" checked={!!auras[i]?.square} onChange={e => setAura(i, { square: e.target.checked })} /> square</label>
          {gm && <label className="chk small"><input type="checkbox" checked={auras[i]?.show !== false} onChange={e => setAura(i, { show: e.target.checked })} /> players see</label>}
        </div>
      ))}
      {gm && (
        <>
          <div className="box-title">Vision & light <span className="dim small">(used when the page has dynamic lighting)</span></div>
          <div className="row gap wrap">
            <label className="chk"><input type="checkbox" checked={!!o.vision} onChange={e => set({ vision: e.target.checked })} /> Has sight</label>
            <label>Sight range <input type="number" value={o.visionRange || ""} placeholder="∞" onChange={e => set({ visionRange: +e.target.value || 0 })} /></label>
            <label>Darkvision <input type="number" value={o.darkvision || ""} onChange={e => set({ darkvision: +e.target.value || 0 })} /></label>
          </div>
          <div className="row gap wrap">
            <label>Bright light <input type="number" value={o.lightBright || ""} onChange={e => set({ lightBright: +e.target.value || 0 })} /></label>
            <label>Dim light <input type="number" value={o.lightDim || ""} onChange={e => set({ lightDim: +e.target.value || 0 })} /></label>
            <span className="dim small">Torch 20/20 · Lantern 30/30 · Light cantrip 20/20</span>
          </div>
          <Perms label="Controlled by" list={o.controlledBy || []} members={members} onChange={l => set({ controlledBy: l })} />
          <div className="box-title">GM notes</div>
          <Field multiline rows={3} value={o.gmNotes || ""} onCommit={v => set({ gmNotes: v })} />
          {ch && (
            <button onClick={() => {
              const { id, x, y, pageId, z, layer, ...rest } = o;
              update("char", ch.id, { defaultToken: rest });
            }}>Save as {ch.name}'s default token</button>
          )}
        </>
      )}
    </div>
  );
}

// ---------------- page settings ----------------
function PageSettings({ p, close }: { p: Page; close: () => void }) {
  const game = useStore(G, s => s.game)!;
  const members = useStore(G, s => s.members);
  const set = (patch: Partial<Page>) => update("page", p.id, patch);
  const num = (k: keyof Page, label: string, step = 1) => (
    <label>{label} <Field type="number" value={p[k] as any} onCommit={v => { const n = parseFloat(v); if (n > 0) set({ [k]: n } as any); }} /></label>
  );
  const chk = (k: keyof Page, label: string, title?: string) => (
    <label className="chk" title={title}><input type="checkbox" checked={!!p[k]} onChange={e => set({ [k]: e.target.checked } as any)} /> {label}</label>
  );
  const splitPlayers = members.filter(m => m.role !== "gm");
  return (
    <div className="pad page-set">
      <label>Name <Field value={p.name} onCommit={v => set({ name: v })} /></label>
      <div className="row gap">
        {num("width", "Width (cells)")}
        {num("height", "Height (cells)")}
        {num("grid", "Cell size (px)")}
      </div>
      <div className="row gap">
        {num("scale", "Each cell is")}
        <label>Units <Field value={p.units} onCommit={v => set({ units: v })} /></label>
        <label>Diagonals
          <select value={p.diagonal} onChange={e => set({ diagonal: e.target.value as any })}>
            <option value="5e">5e (every diagonal = 1)</option>
            <option value="pf">Pathfinder / 3.5 (5-10-5)</option>
            <option value="euclid">Euclidean</option>
            <option value="manhattan">Manhattan</option>
          </select>
        </label>
      </div>
      <div className="row gap wrap">
        <label>Background <input type="color" value={p.bg} onChange={e => set({ bg: e.target.value })} /></label>
        {chk("showGrid", "Show grid")}
        <label>Grid color <input type="color" value={p.gridColor} onChange={e => set({ gridColor: e.target.value })} /></label>
        <label>Grid opacity <input type="range" min={0} max={1} step={0.05} value={p.gridOpacity} onChange={e => set({ gridOpacity: +e.target.value })} /></label>
        {chk("snap", "Snap to grid")}
      </div>
      <div className="box-title">Fog of war</div>
      <div className="row gap wrap">
        {chk("fogEnabled", "Enable fog of war", "Page is hidden from players except areas you reveal with the fog tool")}
        <button onClick={() => set({ fog: [] })}>Hide everything</button>
        <button onClick={() => set({ fog: [{ mode: "reveal", pts: [0, 0, p.width * p.grid, 0, p.width * p.grid, p.height * p.grid, 0, p.height * p.grid] }] })}>Reveal everything</button>
      </div>
      <div className="box-title">Dynamic lighting</div>
      <div className="row gap wrap">
        {chk("lighting", "Enable dynamic lighting", "Players see only what their tokens can see; walls block sight")}
        {chk("globalIllum", "Daylight (global illumination)", "Everything in line of sight is lit — no light sources needed")}
        {chk("explorer", "Explorer mode", "Areas players have seen stay dimly visible")}
        <button onClick={() => { resetExplored(p.id); send({ t: "fx", fx: { kind: "resetExplored", pageId: p.id } }); }}>Reset explored areas</button>
      </div>
      <div className="dim small">Draw walls & doors with the 🧱 tool. Give player tokens “Has sight”, and light sources bright/dim light in the token editor.</div>
      {splitPlayers.length > 0 && (
        <>
          <div className="box-title">Split the party</div>
          <div className="dim small">Players normally follow the ▶ player flag. Tick players to keep them on this page instead.</div>
          {splitPlayers.map(m => (
            <label key={m.uid} className="chk">
              <input type="checkbox" checked={game.state.playerPages[m.uid] === p.id} onChange={e => {
                const pp = { ...game.state.playerPages };
                if (e.target.checked) pp[m.uid] = p.id; else delete pp[m.uid];
                setGame({ playerPages: pp });
              }} /> {m.display}
            </label>
          ))}
        </>
      )}
      <div className="row gap">
        <button onClick={() => duplicatePage(p)}>Duplicate page</button>
        <button className="danger" onClick={async () => { if (await confirm(`Delete page "${p.name}" and everything on it?`)) { remove("page", p.id); close(); } }}>Delete page</button>
      </div>
    </div>
  );
}

async function duplicatePage(p: Page) {
  const { id, ...data } = p;
  const nid = await create("page", { ...data, name: p.name + " (copy)" });
  for (const o of Object.values(G.get().recs.obj)) {
    if (o.pageId !== p.id) continue;
    const { id: _i, ...od } = o;
    create("obj", { ...od, pageId: nid });
  }
}

// ---------------- rollable table ----------------
function TableEditor({ t }: { t: RollTable }) {
  const gm = useIsGM();
  const setItems = (items: RollTable["items"]) => update("table", t.id, { items });
  return (
    <div className="pad">
      {gm && (
        <div className="row gap">
          <label className="grow">Name <Field value={t.name} onCommit={v => update("table", t.id, { name: v.replace(/[\[\]]/g, "") })} /></label>
          <label className="chk"><input type="checkbox" checked={t.showPlayers} onChange={e => update("table", t.id, { showPlayers: e.target.checked })} /> Players can roll</label>
        </div>
      )}
      <div className="dim small">Roll with <code>/r 1t[{t.name}]</code> or inline <code>[[1t[{t.name}]]]</code>.</div>
      <table className="tbl">
        <thead><tr><th>Item</th><th>Weight</th><th /></tr></thead>
        <tbody>
          {t.items.map(it => (
            <tr key={it.id}>
              <td><Field value={it.name} disabled={!gm} onCommit={v => setItems(t.items.map(x => (x.id === it.id ? { ...x, name: v } : x)))} /></td>
              <td><Field type="number" value={it.weight} disabled={!gm} onCommit={v => setItems(t.items.map(x => (x.id === it.id ? { ...x, weight: +v || 0 } : x)))} /></td>
              <td>{gm && <button className="mini" onClick={() => setItems(t.items.filter(x => x.id !== it.id))}>✕</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row gap">
        {gm && <button onClick={() => setItems([...t.items, { id: uid(), name: "New item", weight: 1 }])}>＋ Item</button>}
        {gm && <button onClick={async () => {
          const txt = await prompt("Paste items, one per line (optionally “weight|item”)");
          if (!txt) return;
          const add = txt.split("\n").map(l => l.trim()).filter(Boolean).map(l => {
            const m = l.match(/^(\d+(?:\.\d+)?)\s*\|\s*(.+)$/);
            return { id: uid(), name: m ? m[2] : l, weight: m ? +m[1] : 1 };
          });
          setItems([...t.items, ...add]);
        }}>Bulk add…</button>}
        <button className="primary" onClick={() => sendChat(`/r 1t[${t.name}]`)}>Roll</button>
        {gm && <button className="danger" onClick={async () => { if (await confirm(`Delete table "${t.name}"?`)) remove("table", t.id); }}>Delete</button>}
      </div>
    </div>
  );
}

export { controlsObj };
